import apiClient from '../core';
import type {
  Component,
  ComponentType,
  AddComponentRequest,
  UpdateComponentRequest,
  ReorderComponentsRequest,
  ComponentResponse,
  AddFromLibraryRequest,
  AddFromLibraryResponse,
} from '../../schemas/validation';

export const componentsApi = {
  // Get all components for a page
  async getPageComponents(pageId: string): Promise<ComponentResponse> {
    const response = await apiClient.get<ComponentResponse>(`/pages/${pageId}/components`);
    return response.data;
  },

  // Get a specific component
  async getComponent(
    pageId: string,
    componentId: string
  ): Promise<{ component: Component; pageId: string }> {
    const response = await apiClient.get<{
      component: Component;
      pageId: string;
    }>(`/pages/${pageId}/components/${componentId}`);
    return response.data;
  },

  // Add a custom component
  async addComponent(
    pageId: string,
    data: AddComponentRequest
  ): Promise<{
    message: string;
    component: Component;
    totalComponents: number;
  }> {
    const response = await apiClient.post<{
      message: string;
      component: Component;
      totalComponents: number;
    }>(`/pages/${pageId}/components`, data);
    return response.data;
  },

  // Add a template component
  async addTemplateComponent(
    pageId: string,
    componentType: ComponentType,
    dataOrPosition?: any | number,
    position?: number
  ): Promise<{
    message: string;
    component: Component;
    totalComponents: number;
  }> {
    // Handle overloaded parameters - if dataOrPosition is an object, treat it as data
    let requestBody: any = {};
    if (typeof dataOrPosition === 'object' && dataOrPosition !== null) {
      // dataOrPosition is the data object
      requestBody = { data: dataOrPosition, position };
      console.log('🚀 API: Adding template component with data:', {
        componentType,
        pageId,
        hasData: true,
        dataKeys: Object.keys(dataOrPosition),
        requestBody,
      });
    } else {
      // dataOrPosition is the position number (legacy usage)
      requestBody = { position: dataOrPosition };
      console.log('🚀 API: Adding template component without data:', {
        componentType,
        pageId,
        position: dataOrPosition,
      });
    }

    const response = await apiClient.post<{
      message: string;
      component: Component;
      totalComponents: number;
    }>(`/pages/${pageId}/components/template/${componentType}`, requestBody);
    console.log('✅ API: Template component added:', {
      componentType,
      responseData: response.data,
      componentData: response.data.component?.data,
    });
    return response.data;
  },

  // Update a component
  async updateComponent(
    pageId: string,
    componentId: string,
    data: UpdateComponentRequest
  ): Promise<{ message: string; component: Component }> {
    console.log('🚀 API: Updating component:', {
      pageId,
      componentId,
      data,
      hasData: !!data.updates?.data,
      dataKeys: data.updates?.data ? Object.keys(data.updates.data) : [],
    });
    const response = await apiClient.put<{
      message: string;
      component: Component;
    }>(`/pages/${pageId}/components/${componentId}`, data);
    console.log('✅ API: Component updated:', {
      responseData: response.data,
      componentData: response.data.component?.data,
    });
    return response.data;
  },

  // Delete a component
  async deleteComponent(
    pageId: string,
    componentId: string
  ): Promise<{ message: string; totalComponents: number }> {
    const response = await apiClient.delete<{
      message: string;
      totalComponents: number;
    }>(`/pages/${pageId}/components/${componentId}`);
    return response.data;
  },

  // Reorder components
  async reorderComponents(
    pageId: string,
    data: ReorderComponentsRequest
  ): Promise<{ message: string; components: Component[] }> {
    const response = await apiClient.put<{
      message: string;
      components: Component[];
    }>(`/pages/${pageId}/components/reorder`, data);
    return response.data;
  },

  // Convenience methods for adding specific template components
  async addNavbar(
    pageId: string,
    position?: number
  ): Promise<{
    message: string;
    component: Component;
    totalComponents: number;
  }> {
    return this.addTemplateComponent(pageId, 'navbar', position);
  },

  async addHero(
    pageId: string,
    position?: number
  ): Promise<{
    message: string;
    component: Component;
    totalComponents: number;
  }> {
    return this.addTemplateComponent(pageId, 'hero', position);
  },

  async addFeatures(
    pageId: string,
    position?: number
  ): Promise<{
    message: string;
    component: Component;
    totalComponents: number;
  }> {
    return this.addTemplateComponent(pageId, 'features', position);
  },

  async addCta(
    pageId: string,
    position?: number
  ): Promise<{
    message: string;
    component: Component;
    totalComponents: number;
  }> {
    return this.addTemplateComponent(pageId, 'cta', position);
  },

  async addFooter(
    pageId: string,
    position?: number
  ): Promise<{
    message: string;
    component: Component;
    totalComponents: number;
  }> {
    return this.addTemplateComponent(pageId, 'footer', position);
  },

  async addForm(
    pageId: string,
    position?: number
  ): Promise<{
    message: string;
    component: Component;
    totalComponents: number;
  }> {
    return this.addTemplateComponent(pageId, 'form', position);
  },

  /**
   * Adds a split form (media + form) from the template endpoint.
   * Backend must register `form-split` in the component allowlist, implement
   * `createDefaultComponent('form-split')`, and accept form submissions for
   * this type if the API currently restricts to `type === 'form'`.
   */
  async addFormSplit(
    pageId: string,
    position?: number
  ): Promise<{
    message: string;
    component: Component;
    totalComponents: number;
  }> {
    return this.addTemplateComponent(pageId, 'form-split', position);
  },

  async addTeam(
    pageId: string,
    position?: number
  ): Promise<{
    message: string;
    component: Component;
    totalComponents: number;
  }> {
    return this.addTemplateComponent(pageId, 'team', position);
  },

  // Add component from library
  async addFromLibrary(
    pageId: string,
    data: AddFromLibraryRequest
  ): Promise<AddFromLibraryResponse> {
    const response = await apiClient.post<AddFromLibraryResponse>(
      `/pages/${pageId}/components/from-library`,
      data
    );
    return response.data;
  },
};
